/**
 * @flow
 */
import axios, {AxiosRequestConfig, AxiosResponse, CancelToken} from 'axios';
import Storage from './Storage';
import type {
  ActionSettingsRequest,
  ActionSettingsResponse,
  APIListRequest,
  APIListResponse,
  BranchSettingsRequest,
  BranchSettingsResponse,
  CompanySettingsRequest,
  CompanySettingsResponse,
  DeviceSettingsRequest,
  DeviceSettingsResponse,
  DeviceTypeSettingsRequest,
  DeviceTypeSettingsResponse,
  GroupSettingsRequest,
  GroupSettingsResponse,
  GuidelineSettingsRequest,
  GuidelineSettingsResponse, NotificationLogsRequest, //NotificationLogsResponse,
  SignInRequest,
  SignInResponse,
  SignUpRequest,
  SignUpResponse,
  TimerSettingsRequest,
  TimerSettingsResponse,
  UserSettingsRequest,
  UserSettingsResponse,
  SdkSettingsRequest,
  SdkSettingsResponse,
  ConnectSettingsRequest,
  ConnectSettingsResponse,
  DashboardPatientListRequest,
  DashboardPatientListResponse
} from '../types/api-types';
import Nav from './Nav'
import {ROUTE} from '../settings';
import {Dispatch} from 'redux';
import {openAlertError} from '../redux/reducers/modalReducer';
import {signOut} from '../redux/actions/actions';
//import {setNotiCount} from '../redux/reducers/notificationReducer';

const API_URL_PRD = 'https://uplink.plus';
const API_URL_DEV = 'http://iot.homekling.com';

const API_URL = process.env.NODE_ENV === 'production' ? API_URL_PRD : API_URL_DEV;

export class APIError extends Error {

  constructor(response: AxiosResponse) {
    super(response.statusText);
    this.name = 'APIError';
    this.response = response;
    Error.captureStackTrace(this, APIError);
  }

}

class API {

  // 디스패치 함수를 저장하여 디스패치 함수가 패러미터로 전달되지 않아도 액션을 디스패치할수 있도록함
  /** @private */
  _hackDispatch;

  constructor() {
    process.env.NODE_ENV !== 'production' && console.log('[API] initialized.');
  }

  set dispatch(d) {this._hackDispatch = d}

  async signIn(data: SignInRequest): Promise<SignInResponse> {
    const url = `${API_URL}/login`;
    return this.post<SignInRequest, SignInResponse>(url, {...data, minsDelta: (new Date()).getTimezoneOffset()});
  }

  async signUp(data: SignUpRequest): Promise<SignUpResponse> {
    const url = `${API_URL}/register`;
    return this.post<SignUpRequest, SignUpResponse>(url, data);
  }

  async list(data: APIListRequest): Promise<APIListResponse> {
    const url = `${API_URL}/api/list`;
    return this.post<APIListRequest, APIListResponse>(url, data);
  }

  async companyList() {
    return this.list({action: 'company', data: {parentId: 0}});
  }

  async branchList(companyId: number) {
    return this.list({action: 'branch', data: {parentId: companyId}});
  }

  async branchGroupList(companyId: number) {
    return this.list({action: 'branchngroup', data: {parentId: companyId}});
  }

  async groupList(branchId: number) {
    return this.list({action: 'group', data: {parentId: branchId}});
  }

  async notiCount() {
    const url = `${API_URL}/api/noticount`;
    return this.post(url, {action: 'all'});
  }

  // 디바이스 타입 목록을 반환함 (switch / sensor)
  async deviceTypeList() {
    const url = `${API_URL}/api/device`;
    return this.post<APIListRequest, APIListResponse>(url, {
      action: 'type',
      data: {deviceId: 0}
    });
  }

  async deviceTypeItems(deviceId: number = 0) {
    const url = `${API_URL}/api/device`;
    return this.post<APIListRequest, APIListResponse>(url, {
      action: 'typeitems',
      data: {deviceId}
    });
  }

  // 스위치 목록을 반환함
  async deviceList(deviceId: number = 0) {
    const url = `${API_URL}/api/device`;
    return this.post<APIListRequest, APIListResponse>(url, {
      action: 'list',
      data: {deviceId}
    });
  }

  async deviceGroupTypeItems(deviceId: number = 0, groups) {
    const url = `${API_URL}/api/device`;
    return this.post<APIListRequest, APIListResponse>(url, {
      action: 'grouptypeitems',
      data: {deviceId, groups},
    });
  }

  async companyGroupTypeItems(companyId, groups) {
    const url = `${API_URL}/api/device`;
    return this.post<APIListRequest, APIListResponse>(url, {
      action: 'grouptypeitems',
      data: {companyId, groups},
    });
  }

  async companyBranchGroupTypeItems(companyId, branchId, groups) {
    const url = `${API_URL}/api/device`;
    return this.post<APIListRequest, APIListResponse>(url, {
      action: 'grouptypeitems',
      data: {companyId, branchId, groups},
    });
  }

  async deviceData(deviceId: number, dispatch?: Dispatch) {
    const url = `${API_URL}/api/device`;
    return this.post<APIListRequest, APIListResponse>(url, {
      action: 'data',
      data: {deviceId}
    }, dispatch);
  }

  async deviceCmdList(deviceId: number, dispatch?: Dispatch) {
    const url = `${API_URL}/api/device`;
    return this.post<APIListRequest, APIListResponse>(url, {
      action: 'devicecmdlist',
      data: {deviceId}
    }, dispatch);
  }

  async deviceSendCmd(deviceId: number, cmdType: string, cmdValue: string | number, cmdValueName: string, dispatch?: Dispatch) {
    const url = `${API_URL}/api/device`;
    return this.post<APIListRequest, APIListResponse>(url, {
      action: 'sendcmd',
      data: {deviceId, cmdType, cmdValue, cmdValueName}
    }, dispatch);
  }

  // 대시보드/오버뷰의 모든 데이터를 가져옴
  async dashboardOverviewAll(branchId: number, dispatch?: Dispatch) {
    return this.dashboardOverview('all', branchId);
  }

  // 대시보드/오버뷰의 CountOfBadData 데이터를 가져옴
  async dashboardOverviewCountBad(branchId: number, dispatch?: Dispatch) {
    return this.dashboardOverview('countbad', branchId, dispatch);
  }

  // 대시보드/오버뷰의 Activity 데이터를 가져옴
  async dashboardOverviewActivity(branchId: number, dispatch?: Dispatch) {
    return this.dashboardOverview('activity', branchId, dispatch);
  }

  // 대시보드/오버뷰의 Status 데이터를 가져옴
  async dashboardOverviewStatus(branchId: number, dispatch?: Dispatch) {
    return this.dashboardOverview('status', branchId, dispatch);
  }

  async dashboardOverviewGroupData(branchId: number, dispatch?: Dispatch) {
    return this.dashboardOverview('groupdata', branchId, dispatch);
  }

  async dashboardOverviewItemData(branchId: number, dispatch?: Dispatch) {
    return this.dashboardOverview('itemdata', branchId, dispatch);
  }

  async dashboardOverviewWeeklyData(branchId: number, item: string, dispatch?: Dispatch) {
    return this.post(`${API_URL}/dashboard/overview`, {action: 'weeklydata', data: {branchId, item}}, dispatch);
  }

  async dashboardOverview(type, branchId: number, dispatch?: Dispatch) {
    return this.post(`${API_URL}/dashboard/overview`, {action: type, data: {branchId}}, dispatch);
  }

  async dashboardChart(listData, dispatch?: Dispatch, cancelToken?: CancelToken) {
    return this.post(`${API_URL}/dashboard/chart`, {action: 'all', data: listData}, dispatch, cancelToken);
  }

  async dashboardChartOne(listData, dispatch?: Dispatch, cancelToken?: CancelToken) {
    return this.post(`${API_URL}/dashboard/chartone`, {action: 'all', data: listData}, dispatch, cancelToken);
  }

  async dashboardList(listData, listOption, dispatch?: Dispatch, cancelToken?: CancelToken) {
    return this.post(`${API_URL}/dashboard/list`, {
      action: 'all',
      data: listData,
      listOption: {...defaultListOption, ...listOption},
    }, dispatch, cancelToken);
  }
  
  async dashboardPatientList(data: DashboardPatientListRequest): Promise<DashboardPatientListResponse> {
    return this.queryRows('dashboard/patientlist', data, defaultPatientListRequest);
  }

  async dashboardListForReport(listData, listOption, dispatch?: Dispatch, cancelToken?: CancelToken) {
    return this.post(`${API_URL}/dashboard/list`, {
      action: 'all',
      data: listData,
      listOption: {...defaultListOption, ...listOption, isReport: true},
    }, dispatch, cancelToken);
  }

  async dashboardNoti({data, listOption}, dispatch?: Dispatch, cancelToken?: CancelToken) {
    return this.post(`${API_URL}/log/noti`, {
      action: 'userlist',
      data,
      listOption: {...defaultListOption, ...listOption}
    }, dispatch, cancelToken);
  }

  async dashboardNotiUnreadOnly(companyId, dispatch?: Dispatch, cancelToken?: CancelToken) {
    return this.post(`${API_URL}/log/noti`, {
      action: 'userlist',
      data: {companyId, unreadOnly: true},
      listOption: {...defaultListOption, sortCol: 'cdate', orderBy: 'D', rowCount: 10}
    }, dispatch, cancelToken);
  }

  async dashboardNotiSetRead(nidList: number[], dispatch?: Dispatch, cancelToken?: CancelToken) {
    return this.post(`${API_URL}/log/noti`, {
      action: 'setread',
      data: {nidList},
      listOption: defaultListOption
    }, dispatch, cancelToken);
  }

  // 맵에 표시할 모든 데이터를 쿼리
  async dashboardMapBranch(data, dispatch?: Dispatch, cancelToken?: CancelToken) {
    return this.post(`${API_URL}/dashboard/mapview`, {
      action: 'branch', data,
    }, dispatch, cancelToken);
  }

  // 맵에서 그룹을 클릭했을 때 표시할 데이터를 쿼리
  async dashboardMapGroup(data, dispatch?: Dispatch, cancelToken?: CancelToken) {
    return this.post(`${API_URL}/dashboard/mapview`, {
      action: 'group', data,
    }, dispatch, cancelToken);
  }

  async reportSummary(data, dispatch?: Dispatch, cancelToken?: CancelToken) {
    let timeDiff = new Date().getTimezoneOffset();
    if (timeDiff > 0) timeDiff += 60;
    return this.post(`${API_URL}/report/summary`, {
      action: 'all', data: {...data, timeDiff},
    }, dispatch, cancelToken);
  }

  async reportDaily(data, dispatch?: Dispatch, cancelToken?: CancelToken) {
    let timeDiff = new Date().getTimezoneOffset();
    if (timeDiff > 0) timeDiff += 60;
    return this.post(`${API_URL}/report/daily`, {
      action: 'all', data: {...data, timeDiff},
    }, dispatch, cancelToken);
  }

  async reportMonthly(data, dispatch?: Dispatch, cancelToken?: CancelToken) {
    let timeDiff = new Date().getTimezoneOffset();
    if (timeDiff > 0) timeDiff += 60;
    return this.post(`${API_URL}/report/monthly`, {
      action: 'all', data: {...data, timeDiff},
    }, dispatch, cancelToken);
  }

  async company(data: CompanySettingsRequest): Promise<CompanySettingsResponse> {
    return this.queryRows('setting/company', data, defaultCompanyRequest);
  }

  async branch(data: BranchSettingsRequest): Promise<BranchSettingsResponse> {
    return this.queryRows('setting/branch', data, defaultBranchRequest);
  }

  async group(data: GroupSettingsRequest): Promise<GroupSettingsResponse> {
    return this.queryRows('setting/group', data, defaultGroupRequest);
  }

  async user(data: UserSettingsRequest): Promise<UserSettingsResponse> {
    return this.queryRows('setting/user', data, defaultUserModelRequest);
  }

  async device(data: DeviceSettingsRequest): Promise<DeviceSettingsResponse> {
    return this.queryRows('setting/device', data, defaultDeviceRequest);
  }

  async SDK(data: SdkSettingsRequest): Promise<SdkSettingsResponse> {
    return this.post(`${API_URL}/api/test`, {
      action: 'test',
      data
    })
  }

  async deviceForReport(listOption) {
    return this.post(`${API_URL}/setting/device`, {
      action: 'list',
      data: null,
      listOption: {...defaultListOption, ...listOption, isReport: true},
    });
  }

  async deviceType(data: DeviceTypeSettingsRequest): Promise<DeviceTypeSettingsResponse> {
    return this.queryRows('setting/dtype', data, defaultDeviceTypeRequest);
  }

  async connect(data: ConnectSettingsRequest): Promise<ConnectSettingsResponse> {
    return this.queryRows('setting/dconnect', data, defaultConnectRequest);
  }

  async action(data: ActionSettingsRequest): Promise<ActionSettingsResponse> {
    return this.queryRows('setting/action', data, defaultActionRequest);
  }

  async timer(data: TimerSettingsRequest): Promise<TimerSettingsResponse> {
    return this.queryRows('setting/timer', data, defaultTimerRequest);
  }

  async guideline(data: GuidelineSettingsRequest): Promise<GuidelineSettingsResponse> {
    return this.queryRows('setting/gline', data, defaultGuidelineRequest);
  }

  async postForLogs(path, data, isReport) {
    if (isReport === true) {
      return this.post(`${API_URL}/${path}`, {
        action: 'list',
        data: data.data,
        listOption: {...defaultListOption, ...data.listOption, isReport: true},
      });
    } else {
      return this.queryRows(path, data, defaultLogsRequest);
    }
  }

  async notificationLog(data, isReport = false) {
    return this.postForLogs('log/noti', data, isReport);
  }

  async activityLog(data, isReport = false) {
    return this.postForLogs('log/activity', data, isReport);
  }

  async loginLog(data, isReport = false) {
    return this.postForLogs('log/login', data, isReport);
  }

  async badDataLog(data, isReport = false) {
    return this.postForLogs('log/baddata', data, isReport);
  }

  async errorLog(data, isReport = false) {
    return this.postForLogs('log/error', data, isReport);
  }

  async demoRequests(data, isReport = false) {
    return this.postForLogs('log/demoreq', data, isReport);
  }

  async queryRows<T, R>(path: string, data: T, defaultData: T): Promise<R> {
    const url = `${API_URL}/${path}`;
    // console.log("queryRows의 데이터", data)
    const {listOption, ...rest} = data;
    return this.post<T, R>(url, {
      ...defaultData,
      ...rest,
      listOption: {
        ...defaultData.listOption,
        ...listOption
      }
    });
  }

  async get<T, R>(url: string, dispatch?: Dispatch, cancelToken?: CancelToken) {
    return this.request<T, R>(url, 'GET', dispatch, cancelToken);
  }

  async post<T, R>(url: string, data: T, dispatch?: Dispatch, cancelToken?: CancelToken) {
    return this.request<T, R>(url, 'POST', data, dispatch, cancelToken);
  }

  async request<T, R>(url: string, method: 'GET' | 'POST', data?: T, dispatch?: Dispatch, cancelToken?: CancelToken) {
    try {
      const config = this.getRequestConfig(url, method, data, cancelToken);
      process.env.NODE_ENV !== 'production' && console.info(`[API] request ${url} ${data.action}`, config);
      const res: AxiosResponse<R> = await axios.request<T, R>(config);
      process.env.NODE_ENV !== 'production' && console.info(`[API] response ${url} ${data.action}`, {res});
      // if(res?.status === 200 && res.data) {
      if(res.data) {
        if(res.data?.success === true) {
          // 노티 카운트 API 로 대체됨!
          //setNotiCount(res.data?.notiCount >= 0 ? res.data.notiCount : 0, dispatch ?? this._hackDispatch);
          return res.data;
        } else {
          const {msgcode} = res.data ?? {};
          if(msgcode === 202) {
            // 로그인 페이지로 무조건 이동함
            process.env.NODE_ENV !== 'production' && console.log('[API] 202 received!');
            signOut();
            return {success: false, data: null}
          } else if(msgcode === 203) {
            // 토큰을 업데이트 하고 동일한 요청을 다시 보냄
            Storage.from(res.data);
            return this.request(url, method, data);
          } else if(msgcode === 999) {
            // 999 오류인 경우 강제로 로그아웃하고 로그인 페이지로 이동함
            if(process.env.NODE_ENV === 'production') {
              signOut();
            }
          } else {
            console.error('[API] error', {res});
            throw new APIError(res);
          }
        }
      } else {
        console.error('[API] error', {res});
        throw new APIError(res);
      }
    } catch(error) {
      if(axios.isCancel(error)) {
        console.log('[API] cancelled', {error});
      } else {
        // if (error.isAxiosError && (error.response.status >= 400 && error.response.status < 500)) {
        if (error.isAxiosError && (error.response >= 400 && error.response < 500)) {
          signOut();
          return {success: false, data: null}
        } else {
          if(dispatch) {
            openAlertError(dispatch, error);
            return {success: false, data: null}
          } else {
            throw error;
          }
        }
      }
    }
  }

  getRequestConfig<T>(url: string, method: 'GET' | 'POST', data?: T, cancelToken?: CancelToken): AxiosRequestConfig {
    const token = Storage.token;
    return {
      method,
      url,
      data: data ?? {},
      headers: {
        Authorization: token ? `bearer ${Storage.token}` : undefined,
      },
      cancelToken,
    };
  }

  processAction<S, A>(state: S, action: A, prefix: string): S {
    const {type, ...payload} = action;
    switch(type) {
      case `${prefix}_PENDING`:
        return {status: 'pending'};
      case `${prefix}_DONE`:
        return {status: 'done', ...payload};
      case `${prefix}_ERROR`:
        return {status: 'error', ...payload};
      default:
        return state;
    }
  }

}

// region >>>> API Default Data <<<<

const defaultListOption = {
  word: null,
  page: 1,
  rowCount: 20,
  sortCol: null,
  orderBy: null
};

const defaultCompanyData = {
  id: 0,
  name: null,
  partition: null,
  active: null,
};

const defaultBranchData = {
  id: 0,
  parentId: 0,
  name: null,
  active: null,
};

const defaultGroupData = {
  companyId: 0,
  id: 0,
  parentId: 0,
  name: null,
  active: null,
};

const defaultUserModelData = {
  companyId: 0,
  branchId: 0,
  groupId: 0,
  id: 0,
  name: '',
  active: null,
  email: '',
  pword: '',
  cellno: '',
  permission: 0,
  newPWD: '',
  profileImg: '',
};

const defaultDeviceData = {
  id: 0,
  serial: null,
  name: null,
  active: null,
  groupId: 0,
  typeId: 0,
};

const defaultDeviceTypeData = {
  id: 0,
  name: '',
  active: null,
  gline: null,
  maker: '',
  controller: 'Y' | 'N',
  topic: '',
  partition: '',
};

const defaultActionData = {
  id: 0,
  pid: 0,
  linkedId: 0,
  active: null,
  withTimer: null,
  info: '',
  cmdState: [],
};

const defaultTimerData = {
  id: 0,
  pid: 0,
  active: null,
  info: '',
  startTime: 0,
  endTime: 0,
};

const defaultGuidelineData = {
  id: 0,
  pid: 0,
  gline: '',
};

const defaultNotificationLogData = {
  id: 0,
  ntype: 'noti',
  message: '',
  isdraft: '',
  company_id: 0,
  cdate: 0,
};

const defaultCompanyRequest: CompanySettingsRequest = {
  action: '',
  data: defaultCompanyData,
  listOption: defaultListOption,
};

const defaultBranchRequest: BranchSettingsRequest = {
  action: '',
  data: defaultBranchData,
  listOption: defaultListOption,
};

const defaultGroupRequest: GroupSettingsRequest = {
  action: '',
  data: defaultGroupData,
  listOption: defaultListOption,
};

const defaultUserModelRequest: UserSettingsRequest = {
  action: '',
  data: defaultUserModelData,
  listOption: defaultListOption,
};

const defaultDeviceRequest: DeviceSettingsRequest = {
  action: '',
  data: defaultDeviceData,
  listOption: defaultListOption,
};

const defaultDeviceTypeRequest: DeviceTypeSettingsRequest = {
  action: '',
  data: defaultDeviceTypeData,
  listOption: defaultListOption,
};

const defaultConnectRequest: ConnectSettingsRequest = {
  action: '',
  data: defaultDeviceTypeData,
  listOption: defaultListOption,
};

const defaultPatientListRequest: DashboardPatientListRequest = {
  action: '',
  data: defaultDeviceTypeData,
  listOption: defaultListOption,
};

const defaultActionRequest: DeviceSettingsRequest = {
  action: '',
  data: defaultActionData,
  listOption: defaultListOption,
};

const defaultTimerRequest: TimerSettingsRequest = {
  action: '',
  data: defaultTimerData,
  listOption: defaultListOption,
};

const defaultGuidelineRequest : GuidelineSettingsRequest = {
  action: '',
  data: defaultGuidelineData,
  listOption: defaultListOption,
};

const defaultNotificationLogsRequest : NotificationLogsRequest = {
  action: '',
  data: defaultNotificationLogData,
  listOption: defaultListOption,
};

const defaultLogsRequest = {
  action: 'list',
  listOption: defaultListOption,
};


// endregion

export default new API();
